import { Container, MuiThemeProvider } from '@material-ui/core';
import { DataGrid, GridColDef, GridValueFormatterParams } from '@mui/x-data-grid';
import moment from 'moment';
import React, { useEffect, useMemo } from 'react';
import { connect } from 'react-redux';
import { useLocation, useParams } from 'react-router-dom';
import { BreadCrumbData, Error } from '@redux/common/types';
import { getDeviceErrorSelector, getDeviceFactoryResetHistoryByDeviceId, isLoadingDeviceFactoryResetHistory } from '@redux/device/deviceSelectors';
import { getDeviceFactoryResetHistoryThunk } from '@redux/device/deviceThunks';
import { DeviceFactoryResetLog } from '@redux/device/deviceTypes';
import { appendBreadcrumbAction } from '@redux/oauth/oauthActions';
import { getBreadcrumbsSelector } from '@redux/oauth/oauthSelectors';
import { ReduxState } from '@redux/types';
import { GridToolbar } from '../../components/GridToolbar/GridToolbar';
import { Header } from '../../components/Header/Header';
import { defaultDeviceBreadCrumb, defaultDeviceBreadCrumbs } from '../../utils/breadcrumbs';
import { defaultTheme } from '../../utils/styles';
import style from './DeviceFactoryResetHistory.scss';

interface Props {
  factoryResetHistoryByDeviceId: (deviceId: string) => DeviceFactoryResetLog[];
  isLoading: boolean;
  error: Record<string, Error>;
  breadCrumbs: BreadCrumbData[];
  getFactoryResetHistory: (deviceId: string) => void;
  appendBreadCrumbs: (breadCrumb: BreadCrumbData, defaultCrumbHistory: BreadCrumbData[]) => void;
}

const DeviceFactoryResetHistory: React.FC<Props> = ({ factoryResetHistoryByDeviceId, isLoading, error, breadCrumbs, getFactoryResetHistory, appendBreadCrumbs }) => {
  const { deviceId } = useParams();
  const location = useLocation();

  useEffect(() => {
    if (deviceId) {
      getFactoryResetHistory(deviceId);
    }
  }, [deviceId, getFactoryResetHistory]);

  const rows = deviceId ? factoryResetHistoryByDeviceId(deviceId) || [] : [];

  const columns: GridColDef[] = useMemo(
    () => [
      { field: 'id', headerName: 'Log ID', width: 330 },
      { field: 'status', headerName: 'Status', width: 140 },
      { field: 'personId', headerName: 'Person ID', width: 320 },
      { field: 'deviceId', headerName: 'Device ID', width: 320 },
      {
        field: 'created',
        headerName: 'Created',
        width: 200,
        valueFormatter: (params: GridValueFormatterParams) => (params.value ? moment(params.value as string).format('YYYY-MM-DD HH:mm') : '-')
      },
      {
        field: 'modifiedDate',
        headerName: 'Modified',
        width: 200,
        valueFormatter: (params: GridValueFormatterParams) => (params.value ? moment(params.value as string).format('YYYY-MM-DD HH:mm') : '-')
      }
    ],
    []
  );

  const errorMessage = Object.values(error || {})?.[0]?.message;

  return (
    <MuiThemeProvider theme={defaultTheme}>
      <div className={style.root}>
        <Container maxWidth={false}>
          <Header
            breadCrumbs={breadCrumbs}
            title={'Factory Reset History'}
            subtitle={deviceId ? `Device ${deviceId}` : undefined}
            appendBreadCrumbs={appendBreadCrumbs}
            disableTitleGutters
            defaultCurrentCrumb={defaultDeviceBreadCrumb}
            defaultCrumbHistory={defaultDeviceBreadCrumbs}
            overrideTitle={'Factory Reset History'}
            overrideUrl={location?.pathname}
          />
          <div className={style.dataGrid}>
            <DataGrid rows={rows} columns={columns} loading={isLoading} pagination disableSelectionOnClick components={{ Toolbar: GridToolbar }} />
          </div>
          {errorMessage && <div className={style.errorMessage}>{errorMessage}</div>}
        </Container>
      </div>
    </MuiThemeProvider>
  );
};

const connectRedux = connect(
  (state: ReduxState) => ({
    factoryResetHistoryByDeviceId: (deviceId: string) => getDeviceFactoryResetHistoryByDeviceId(state, deviceId),
    isLoading: isLoadingDeviceFactoryResetHistory(state),
    error: getDeviceErrorSelector(state),
    breadCrumbs: getBreadcrumbsSelector(state)
  }),
  (dispatch: Function) => ({
    getFactoryResetHistory: (deviceId: string) => {
      dispatch(getDeviceFactoryResetHistoryThunk(deviceId));
    },
    appendBreadCrumbs: (breadCrumbData: BreadCrumbData, defaultCrumbHistory: BreadCrumbData[]) => {
      dispatch(appendBreadcrumbAction({ breadCrumbData, defaultCrumbHistory }));
    }
  })
);

export default connectRedux(DeviceFactoryResetHistory) as React.ComponentType;
